import * as React from 'react';
import cx from 'classnames';
import { split, last } from 'lodash';

import { Link, Route, Redirect, Switch, useParams, useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { Spin, Typography, Button, Menu, Dropdown, Tooltip, message, Modal } from 'antd';
import { DownOutlined, StopOutlined } from '@ant-design/icons';
import { EditUserDrawer } from './EditUserDrawer';

import {
  useGetUserById,
  useSaveUser,
  useDeleteUserById,
  useMarkUserEmailAsVerified,
  useSendVerificationEmail,
  useSendResetPasswordEmail,
} from '@frontend/app/hooks';

import { lazyLoadComponent } from '@frontend/app/utils/lazyLoadComponent';
import { DeleteUserMutation_deleteUserById_resources } from '@frontend/app/queries/types/DeleteUserMutation';
import styles from './UserDetailView.scss';

const { lazy, useState, useMemo, useCallback } = React;
const { Title } = Typography;

const UserDetails = lazy(() => import('./UserDetailView/UserDetails'));
const UserClients = lazy(() => import('./UserDetailView/UserClients'));

interface IMatchParams {
  userId: string;
}
interface IProps {
  className?: string;
}

/**
 * @type {React.FunctionComponent}
 */
const UserDetailView: React.FunctionComponent<React.PropsWithChildren<IProps>> = React.memo((props) => {
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();
  const { userId } = useParams<IMatchParams>();
  const { saveUser, loading: saving } = useSaveUser();
  const { markUserEmailAsVerified } = useMarkUserEmailAsVerified();
  const { sendVerificationEmail } = useSendVerificationEmail();
  const { sendResetPasswordEmail } = useSendResetPasswordEmail();
  const { deleteUserById } = useDeleteUserById();

  const { loading, user, error, refetch } = useGetUserById({
    variables: {
      id: userId,
    },
  });

  const lastRoute = last(split(location.pathname, '/'));
  const userEmailVerified = useMemo(() => user?.auth0User?.emailVerified, [user]);
  const hasUsernamePasswordConnection = useMemo(() => user?.auth0User?.hasUsernamePasswordConnection, [user]);
  const [selectedRoute, setSelectedRoute] = useState<string>(
    lastRoute && ['details', 'clients'].includes(lastRoute) ? lastRoute : 'details',
  );
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [blockedModalVisible, setBlockedModalVisible] = useState(false);
  const [blockedResources, setBlockedResources] = useState<DeleteUserMutation_deleteUserById_resources[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteUser = useCallback(async () => {
    if (!user) return;
    setIsDeleting(true);

    try {
      const result = await deleteUserById({
        variables: {
          id: user.id,
        },
      });

      const deleteResult = result.data?.deleteUserById;

      if (deleteResult?.blocked && deleteResult.resources) {
        // User is linked to Shopify resources — block deletion and show info modal
        setBlockedResources(deleteResult.resources);
        setBlockedModalVisible(true);
      } else if (deleteResult?.success) {
        history.push('/users');
      } else if (deleteResult && !deleteResult.success) {
        // Handle non-blocked failures (e.g., validation errors, backend failures)
        message.error(deleteResult.message || 'Failed to delete user. Please try again.');
      }
    } catch (error) {
      // Handle network-level errors (timeouts, 500s, etc.)
      message.error('Failed to delete user. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [user, deleteUserById, history]);

  const handleMenuItemClick = async ({ key }: { key: string }) => {
    switch (key) {
      case 'edit': {
        setOpenUpdateModal(true);

        break;
      }
      // mark user email as verified
      case 'mark_email_verified': {
        if (!user) return;
        await markUserEmailAsVerified({ variables: { userId: user.id } });
        message.success('User email is now verified.');

        refetch();

        break;
      }
      // verification email
      case 'verify': {
        if (!user) return;
        sendVerificationEmail({ variables: { userId: user.id } });
        message.success('Verification email has been sent.');

        break;
      }
      // reset password email
      case 'reset': {
        if (!user) return;
        sendResetPasswordEmail({ variables: { email: user.email } });
        message.success('Reset password email has been sent.');

        break;
      }
      case 'delete': {
        await handleDeleteUser();

        break;
      }
      default: {
        return;
      }
    }
  };
  const handleNavClick = ({ key }: { key: string }) => setSelectedRoute(key);

  if (error) {
    return <div>There's an error when trying to load the user detail.</div>;
  }

  const ActionMenu = (
    <Menu onClick={handleMenuItemClick}>
      <Menu.Item key="edit">Edit</Menu.Item>
      <Menu.Item key="mark_email_verified" disabled={userEmailVerified}>
        Mark user email as verified
      </Menu.Item>
      <Menu.Item disabled={!hasUsernamePasswordConnection || userEmailVerified} key="verify">
        <Tooltip
          title={
            !hasUsernamePasswordConnection
              ? `Cannot send verification email to user that has no 'Username-Password-Authentication' connection.`
              : userEmailVerified
                ? 'This user has already verified their email.'
                : ''
          }
          overlayStyle={{
            display: hasUsernamePasswordConnection && !userEmailVerified ? 'none' : undefined,
          }}
        >
          <p>Send verification email</p>
        </Tooltip>
      </Menu.Item>
      <Menu.Item disabled={!hasUsernamePasswordConnection} key="reset">
        <Tooltip
          title="Cannot send reset password email to user that has no 'Username-Password-Authentication' connection."
          overlayStyle={{
            display: hasUsernamePasswordConnection ? 'none' : undefined,
          }}
        >
          <p>Send reset password email</p>
        </Tooltip>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="delete" disabled={isDeleting}>
        {isDeleting ? 'Deleting...' : 'Delete'}
      </Menu.Item>
    </Menu>
  );

  return (
    <div className={cx(styles.UserDetailView, props.className)}>
      {loading && <Spin size="large" />}
      {!loading && user && (
        <>
          <div className={styles.header}>
            <Title>{`${user.name} <${user.email}>`}</Title>
            <Dropdown overlay={ActionMenu} trigger={['click']}>
              <Button className={styles.action}>
                Actions <DownOutlined />
              </Button>
            </Dropdown>
          </div>
          <div className={styles.children}>
            <Menu className={styles.menu} onClick={handleNavClick} mode="horizontal" selectedKeys={[selectedRoute]}>
              <Menu.Item key="details">
                <Link to={`${match.url}/details`}>Details</Link>
              </Menu.Item>
              <Menu.Item key="clients">
                <Link to={`${match.url}/clients`}>Clients</Link>
              </Menu.Item>
            </Menu>
            <Switch>
              <Route
                path={`${match.path}/details`}
                render={(props) => lazyLoadComponent(UserDetails)({ ...props, user: user })}
              />
              <Route
                path={`${match.path}/clients`}
                render={(props) => lazyLoadComponent(UserClients)({ ...props, user: user })}
              />
              <Redirect from={`${match.path}`} to={`${match.path}/details`} />
            </Switch>
          </div>
          <EditUserDrawer
            user={user}
            open={openUpdateModal}
            onRequstClose={() => setOpenUpdateModal(false)}
            isSubmitting={saving}
            onSubmit={(params) => {
              saveUser({
                variables: {
                  user: {
                    ...params,
                    id: user.id,
                  },
                },
              }).then(() => setOpenUpdateModal(false));
            }}
          />
          <Modal
            title={
              <span>
                <StopOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                Deletion Blocked — Shopify Resources Linked
              </span>
            }
            open={blockedModalVisible}
            onCancel={() => setBlockedModalVisible(false)}
            footer={[
              <Button key="ok" type="primary" onClick={() => setBlockedModalVisible(false)}>
                OK
              </Button>,
            ]}
          >
            <p>
              This user cannot be deleted because they are linked to{' '}
              <strong>{blockedResources.length}</strong> active Shopify store(s):
            </p>
            <ul style={{ margin: '12px 0', paddingLeft: 20 }}>
              {blockedResources.map((resource) => (
                <li key={resource.id} style={{ marginBottom: 4 }}>
                  <strong>{resource.name}</strong> (Client: {resource.clientName})
                </li>
              ))}
            </ul>
            <p>
              Please reassign or disconnect the Shopify resource(s) from this user before attempting deletion.
              To do this, update the resource owner in the Shopify Admin for the relevant store(s), then retry deletion.
            </p>
          </Modal>
        </>
      )}
    </div>
  );
});

export default UserDetailView;
